import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"

/**
 * Information about a PDF document to display.
 */
export type PdfInfo = {
  staticUrl: string
  title: string
  pageNumber?: number
}

/**
 * Value exposed by the PDF viewer context.
 */
export type PdfContextValue = {
  currentPdf: PdfInfo | null
  openPdf: (params: { staticUrl: string; title: string; pageNumber?: number }) => void
  closePdf: () => void
  isVisible: boolean
}

const PdfContext = createContext<PdfContextValue | null>(null)

/**
 * Props accepted by the PdfViewerProvider component.
 */
type PdfViewerProviderProps = {
  children: ReactNode
}

/**
 * Provider that manages which PDF is currently open.
 *
 * Only one PDF can be displayed at a time. Calling `openPdf` replaces the
 * previously open document.
 */
export function PdfViewerProvider({ children }: PdfViewerProviderProps) {
  const [currentPdf, setCurrentPdf] = useState<PdfInfo | null>(null)
  const [isVisible, setIsVisible] = useState(false)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const openPdf = useCallback(
    ({ staticUrl, title, pageNumber }: { staticUrl: string; title: string; pageNumber?: number }) => {
      // Cancel any pending close timer to prevent it from wiping the new PDF
      if (closeTimerRef.current !== null) {
        clearTimeout(closeTimerRef.current)
        closeTimerRef.current = null
      }
      setCurrentPdf({ staticUrl, title, pageNumber })
      setIsVisible(true)
    },
    [],
  )

  const closePdf = useCallback(() => {
    setIsVisible(false)
    // Delay clearing currentPdf to allow close animation (200ms matches transition duration)
    closeTimerRef.current = setTimeout(() => {
      setCurrentPdf(null)
      closeTimerRef.current = null
    }, 200)
  }, [])

  // Cleanup pending timer on unmount
  useEffect(() => {
    return () => {
      if (closeTimerRef.current !== null) {
        clearTimeout(closeTimerRef.current)
      }
    }
  }, [])

  const value = useMemo<PdfContextValue>(
    () => ({ currentPdf, openPdf, closePdf, isVisible }),
    [currentPdf, openPdf, closePdf, isVisible],
  )

  return <PdfContext.Provider value={value}>{children}</PdfContext.Provider>
}

/**
 * Hook to consume the PDF viewer context.
 *
 * Must be called inside a `PdfViewerProvider`. Throws an error if used
 * outside of the provider tree.
 */
export function usePdfViewer(): PdfContextValue {
  const ctx = useContext(PdfContext)
  if (!ctx) {
    throw new Error("usePdfViewer must be used within a PdfViewerProvider")
  }
  return ctx
}
